import { clone, compose, filter, is, map, path } from 'ramda'
import HttpCacheAbstractService from './HttpCacheAbstractService'
import service from '../dictionary/service.json'

const isArray = is(Array)

/**
 * Cache service that handles both individual items and those loaded for another entity.
 */
export default class HttpCacheGroupService extends HttpCacheAbstractService {
  /**
   * Constructor
   *
   * @param {HttpCacheItemService} itemService
   * @param {function} getServiceArgsFunction
   * @param {function} getServiceArgsValue
   * @param {function} iriFactory
   */
  constructor(itemService, getServiceArgsFunction, getServiceArgsValue, iriFactory) {
    super(service.group)
    this.itemService = itemService
    this.getServiceArgsFunction = getServiceArgsFunction
    this.getServiceArgsValue = getServiceArgsValue
    this.iriFactory = iriFactory
  }

  /**
   * Reads the cache pool for matches for the current request.
   *
   * @param {String} path
   * @param {Object} parameters
   * @param {HttpCachePoolConfig} poolConfig
   * @param {Array<HttpCacheItem>} cachePool
   * @return {HttpCacheItem|Array<HttpCacheItem>|null}
   */
  read({ path, parameters, poolConfig }, cachePool) {
    if (this.isGroupRequest(path, parameters, poolConfig)) {
      const groupItem = this.itemService.read({ path, parameters, poolConfig, itemType: `${poolConfig.name}group` }, cachePool)
      if (groupItem === null || isArray(groupItem)) {
        return null
      }

      const groupIdProvider = this.getServiceArgsFunction(poolConfig, 'groupIdProvider')

      return filter((item) => item.type === poolConfig.name && groupIdProvider(item.value) === groupItem.id, cachePool)
    }

    const idList = this.idListProvider(parameters, poolConfig)

    if (idList) {
      const pathFactory = this.iriFactory(path)
      const idCount = idList.length

      const result = compose(
        filter(Boolean),
        map((id) => {
          const hit = this.itemService.read({ path: pathFactory(id), parameters, poolConfig }, cachePool)

          if (hit) {
            idList.splice(idList.indexOf(id), 1)
          }

          return hit
        }),
        clone,
      )(idList)

      if (result.length !== idCount) {
        this.partialResult = true
      }

      return result
    }

    return this.itemService.read({ path, parameters, poolConfig }, cachePool)
  }

  /**
   * Writes to the cache pool.
   *
   * @param {String} path
   * @param {Object} parameters
   * @param {HttpCachePoolConfig} poolConfig
   * @param {Number} currentTimestamp
   * @param {Array<HttpCacheItem>} cachePool
   * @param {*} valueOrValues
   * @return {Array<HttpCacheItem>}
   */
  write({ path, parameters, poolConfig, currentTimestamp }, cachePool, valueOrValues) {
    let items = cachePool

    if (this.isGroupRequest(path, parameters, poolConfig)) {
      const id = this.getServiceArgsFunction(poolConfig, 'requestIdProvider')(path, parameters)
      items = this.itemService.write({ path, parameters, poolConfig, itemType: `${poolConfig.name}group`, currentTimestamp }, cachePool, { id })
    }

    return this.itemService.write({ path, parameters, poolConfig, currentTimestamp }, items, valueOrValues)
  }

  /**
   * Identify whether the current request is for a group.
   *
   * @param {string} path
   * @param {Object} parameters
   * @param {HttpCachePoolConfig} poolConfig
   * @return {boolean}
   */
  isGroupRequest(path, parameters, poolConfig) {
    return this.getServiceArgsFunction(poolConfig, 'isGroupRequest')(path, parameters)
  }

  /**
   * Identify whether the current request is indexing multiple entities with an ID list specified.
   *
   * @param {Object} parameters
   * @param {HttpCachePoolConfig} poolConfig
   * @return {Array|null}
   */
  idListProvider(parameters, poolConfig) {
    const idParameter = path([this.getIdParameterName(poolConfig)], parameters)
    return isArray(idParameter) ? idParameter : null
  }

  /**
   * Gets the ID parameter name.
   *
   * @param {HttpCachePoolConfig} poolConfig
   * @return {string}
   */
  getIdParameterName(poolConfig) {
    return this.getServiceArgsValue(poolConfig, 'idParameterName', 'id')
  }
}
